import { useTranslation } from "react-i18next"

import type { ChatSessionModel } from "@/api/models"

import { Bell, Crown, UserPlus, X } from "@/components/icons"
import { useMarkSessionNotificationViewed } from "@/hooks/use-session-sharing"

type ChatUpdatesStripProps = {
  sessions: ChatSessionModel[]
  onOpen: (session: ChatSessionModel) => void
}

/**
 * Dismissible "Updates" strip at the top of the Chats section. Surfaces new
 * shares and ownership transfers (sessions flagged hasUnviewedNotification),
 * sharing the same unviewed state as the per-item dot — opening or dismissing
 * marks the notification viewed via the existing API.
 */
export function ChatUpdatesStrip({ sessions, onOpen }: ChatUpdatesStripProps) {
  const { t } = useTranslation()
  const markViewed = useMarkSessionNotificationViewed()

  const updates = sessions.filter((session): session is NonNullable<ChatSessionModel> =>
    Boolean(session?.hasUnviewedNotification && session.invitationNotification)
  )

  if (updates.length === 0) {
    return null
  }

  const dismissAll = () => {
    updates.forEach((session) => {
      if (session.id) markViewed.mutate(session.id)
    })
  }

  return (
    <div className="border-warning/25 bg-warning/10 mx-1 mb-2 rounded-md border p-1">
      <div className="flex items-center gap-1.5 px-1.5 pt-0.5 pb-1">
        <Bell className="text-warning-foreground size-3 shrink-0" />
        <span className="text-warning-foreground flex-1 text-xs font-semibold">
          {t("chatsession.updates", { defaultValue: "Updates" })} · {updates.length}
        </span>
        <button
          aria-label={t("chatsession.dismiss_updates", { defaultValue: "Dismiss updates" })}
          className="text-muted-foreground hover:text-foreground shrink-0 transition-colors"
          type="button"
          onClick={dismissAll}
        >
          <X className="size-3.5" />
        </button>
      </div>
      <div className="flex flex-col">
        {updates.map((session) => {
          const isOwner = session.invitationNotification?.role === "owner"
          const name =
            session.invitationNotification?.invitedByDisplayName ||
            t("chatsession.someone", { defaultValue: "Someone" })
          return (
            <button
              key={session.id}
              className="hover:bg-sidebar-accent/40 flex w-full items-center gap-2 rounded px-1.5 py-1 text-left transition-colors"
              type="button"
              onClick={() => {
                if (session.id) markViewed.mutate(session.id)
                onOpen(session)
              }}
            >
              {isOwner ? (
                <Crown className="text-warning size-3.5 shrink-0" />
              ) : (
                <UserPlus className="text-muted-foreground size-3.5 shrink-0" />
              )}
              <span className="text-sidebar-foreground min-w-0 flex-1 truncate text-xs">
                {isOwner
                  ? t("chatsession.update_made_owner", {
                      name,
                      defaultValue: "{{name}} made you owner",
                    })
                  : t("chatsession.update_shared", {
                      name,
                      defaultValue: "{{name}} shared a chat",
                    })}
              </span>
            </button>
          )
        })}
      </div>
    </div>
  )
}
